import type Alpine from 'alpinejs';
import Cookies from 'js-cookie';

declare var Livewire: {
    emit(event: string, ...args: any[]): void;
    emitTo(component: string, event: string, ...args: any[]): void;
    emitUp(event: string, ...args: any[]): void;
    on(event: string, callback: (...args: any[]) => void): void;
    off(event: string, callback?: (...args: any[]) => void): void;
    hook(hook: string, callback: Function): void;
    restart(): void;
};

document.addEventListener('alpine:init', () => {
    Alpine.data('tooltip', () => ({
        show: false,
        text: '',
        top: 0,
        left: 0,
        arrowLeft: 50,
        placement: 'top',
        init() {
            const hide = (e: MouseEvent) => {
                this.show = false;

                const target = (e.target as HTMLElement);
                if (!target || !('closest' in target)) return;

                target.closest('[title], [aria-label]')?.removeEventListener('mouseleave', hide);
            };

            document.addEventListener('mouseover', (e: MouseEvent) => {
                const target = (e.target as HTMLElement).closest('[title], [aria-label]');
                if (!target) return;

                if (target.hasAttribute('title')) {
                    target.setAttribute('aria-label', target.getAttribute('title') || '');
                    target.removeAttribute('title');
                }

                this.text = target.getAttribute('aria-label') || '';
                if (!this.text) return;

                this.show = true;

                this.$nextTick(() => {
                    const rect = target.getBoundingClientRect();
                    const tooltip = this.$el as HTMLElement;

                    const tipWidth = tooltip.offsetWidth;
                    const tipHeight = tooltip.offsetHeight;
                    const margin = 12;

                    let idealLeft = rect.left + (rect.width / 2);
                    let actualLeft = idealLeft;

                    if (idealLeft - (tipWidth / 2) < margin) {
                        actualLeft = (tipWidth / 2) + margin;
                    } else if (idealLeft + (tipWidth / 2) > window.innerWidth - margin) {
                        actualLeft = window.innerWidth - (tipWidth / 2) - margin;
                    }

                    this.left = actualLeft;
                    this.arrowLeft = Math.max(10, Math.min(90, 50 + (((idealLeft - this.left) / tipWidth) * 100)));

                    if (rect.top - tipHeight - margin > 0) {
                        this.top = rect.top - 8;
                        this.placement = 'top';
                    } else {
                        this.top = rect.bottom + tipHeight + 8;
                        this.placement = 'bottom';
                    }
                });

                target.addEventListener('mouseleave', hide);
            });

            window.addEventListener('scroll', hide, { passive: true });

            window.addEventListener('click', hide, { passive: true });
        },
    }));

    Alpine.data('appearance', () => ({
        isDark: Cookies.get('appearance') == 'dark',
        init() {
            this.$watch('isDark', () => {
                this.setAppearance();
            });

            this.setAppearance();
        },
        toggle() {
            this.isDark = !this.isDark;
        },
        setAppearance() {
            Cookies.set('appearance', this.isDark ? 'dark' : 'light');

            if (this.isDark) {
                document.documentElement.classList.add('dark');
                document.getElementById('pwa-theme-color').setAttribute('content', '#262626');
            } else {
                document.documentElement.classList.remove('dark');
                document.getElementById('pwa-theme-color').setAttribute('content', '#ffffff');
            }
        },
    }));

    Alpine.data('modelsSidebar', () => ({
        clickedSlug: null as string | null,
        hovered: false,
        timeout: null as number | NodeJS.Timeout | null,

        init() {
            this.poll();
        },

        destroy() {
            if (this.timeout) {
                clearTimeout(this.timeout as number);
            }
        },

        async poll() {
            try {
                if (!this.hovered) {
                    await this.$wire.refreshData();
                }
            } finally {
                this.timeout = setTimeout(() => this.poll(), 2 * 60 * 1000);
            }
        },

        handleClick(event: Event) {
            const target = event.target as HTMLElement;
            const item = target.closest('[data-slug]') as HTMLElement;

            if (item) {
                this.clickedSlug = item.dataset.slug;
                this.$dispatch('chat-selected', item.dataset.slug);
            }
        },

        handleChatSelected(event: CustomEvent) {
            this.clickedSlug = event.detail;
        },

        handleNavigated() {
            this.clickedSlug = null;
        },
    }));

    Alpine.data('modelsCardGroup', () => ({
        offlineImage: '/assets/offline/offline.png',
        loadPreviewImage($el: HTMLElement, previewSrc: string, avatarSrc: string) {
            const queue = [previewSrc, avatarSrc, this.offlineImage].filter(Boolean);

            const tryNext = () => {
                if (queue.length === 0) return;

                const src = queue.shift();
                const img = new Image();

                img.onload = () => {
                    $el.style.backgroundImage = `url('${src}')`;
                };

                img.onerror = () => {
                    tryNext();
                };

                img.src = src;
            };

            tryNext();
        },
    }));
});

document.addEventListener('livewire:init', () => {
    Livewire.hook('request.error', ({ status, preventDefault }) => {
        if ([502, 503, 504].includes(status)) {
            setTimeout(() => {
                window.location.reload();
            }, 2500);
        }

        if (status === 419) {
            preventDefault();
            window.location.reload();
        }
    });

    Livewire.on('sidebar:scroll', ({ model, action }) => {
        if(!model) return;

        const $scrollbar = document.querySelector('.chat-left-sidebar > .scrollbar');
        if (!$scrollbar) return;

        const $el = $scrollbar.querySelector(`li#model-${model}`);
        if (!$el) return;

        if (action == 'nextPage') {
            const elementBottom = $el.getBoundingClientRect().bottom - $scrollbar.getBoundingClientRect().top + $scrollbar.scrollTop;
            $scrollbar.scrollTo({
                top: elementBottom - $scrollbar.clientHeight + $el.clientHeight
            });
        }

        if (action == 'prevPage') {
            $scrollbar.scrollTo({
                top: $el.getBoundingClientRect().top - $scrollbar.getBoundingClientRect().top + $scrollbar.scrollTop - $el.clientHeight / 2
            });
        }
    });
});

window.addEventListener('chat:show-all', () => {
    const $body = document.querySelector('body');

    $body.classList.toggle('show-chats');

    if (!$body.classList.contains('show-chats')) {
        $body.style.height = '100vh';
        $body.style.overflow = 'hidden';
    } else {
        $body.style.height = null;
        $body.style.overflow = null;
    }
});

import './app.pwa';

import './cookie-consent';
